<template>
  <div class="container-fluid px-4 mt-4">
    <div v-if="loading" class="loading-overlay">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
      <p>Yükleniyor...</p>
    </div>

    <!-- Ürün listesi ve sepet paneli aynı satırda -->
    <div class="row" v-show="!loading">
      <h1 v-show="selectedCategoryName" class="text-xl font-semibold text-gray-800 mb-2 text-center">
        {{ selectedCategoryName }}
      </h1>

      <!-- Ürün listesi -->
      <div class="col-lg-8 col-md-7 col-12 mb-4">
        <vue-good-table
            mode="remote"
            @on-column-filter="onColumnFilter($event.columnFilters)"
            @on-page-change="onPageChange"
            :rows="rows"
            :columns="columns"
            :totalRows="totalRecords"
            :line-numbers="true"
            :pagination-options="{
            enabled: false,
            perPage: 100,
            perPageDropdown: [],
            dropdownAllowAll: false,
            mode: 'records',
            nextLabel: 'Sonraki',
            prevLabel: 'Önceki',
            rowsPerPageLabel: 'Sayfa başına satır',
            ofLabel: '/',
            pageLabel: 'sayfa',
            allLabel: 'Tümü'
          }"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'iconUrl' && props.formattedRow.iconUrl">
              <img
                  :src="props.formattedRow.iconUrl"
                  alt="Ürün Görseli"
                  class="rounded shadow-sm cursor-pointer"
                  style="width: 40px; height: 40px; object-fit: cover"
                  @click="openImageModal(props.formattedRow.url)"
              />
            </span>
            <span v-else-if="props.column.field === 'actions'">
              <div class="d-flex align-items-center flex-nowrap">
                <div v-if="props.row.productOptions && props.row.productOptions.length > 0">
                  <select v-model.number="quantitySelections[props.row.id]" class="form-select form-select-sm me-2" style="width: 70px;">
                    <option v-for="opt in props.row.productOptions" :key="opt" :value="opt">{{ opt }}</option>
                  </select>
                </div>
                <div v-else class="me-2">
                  <input type="number" v-model.number="quantitySelections[props.row.id]" class="form-control form-control-sm" style="width: 70px;" min="1">
                </div>
                <b-button size="sm" variant="success" class="me-1" @click="addToBasket(props.row)">+</b-button>
                <b-button size="sm" variant="danger" @click="removeFromBasket(props.row)">–</b-button>
              </div>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>

      <!-- Sepet paneli -->
      <div class="col-lg-4 col-md-5 col-12 mb-4">
        <div class="card">
          <div class="card-header">
            <h5>Sepet</h5>
          </div>
          <div class="card-body">
            <ul class="list-group mb-3" v-if="basket.length > 0">
              <li
                  class="list-group-item d-flex justify-content-between align-items-center"
                  v-for="item in basket"
                  :key="item.id"
              >
                <div>
                  {{ item.quantity }} x {{ item.name }} - {{ formatPrice(item.totalPrice) }}
                </div>
                <b-button variant="outline-danger" size="sm" @click="removeItemFromBasket(item.id)">
                  &times;
                </b-button>
              </li>
            </ul>

            <div v-else class="text-muted">Sepet boş.</div>

            <div class="mt-3">
              <strong>Toplam: {{ totalBasketPrice }}</strong>
            </div>

            <b-button class="mt-3" variant="primary" block @click="$bvModal.show('order-confirm-modal')" :disabled="basket.length === 0">
              Siparişi Oluştur
            </b-button>

            <b-button class="mt-2" variant="danger" block @click="clearBasket" :disabled="basket.length === 0">
              Sepeti Temizle
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="order-confirm-modal" title="Siparişi Onayla" @ok="confirmCreateOrder" ok-title="Siparişi Onayla" cancel-title="Vazgeç" size="lg" scrollable>
      <b-row>
        <b-col cols="6" v-for="item in basket" :key="item.id">
          <b-card class="mb-2">
            <div class="d-flex justify-content-between">
              <div>
                <strong>{{ item.name }}</strong>
                <p class="mb-0 text-muted">Adet: {{ item.quantity }}</p>
              </div>
              <div class="text-right">
                {{ formatPrice(item.totalPrice) }}
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
      <div class="text-right mt-3">
        <strong>Toplam: {{ totalBasketPrice }}</strong>
      </div>
    </b-modal>

    <b-modal id="image-modal" size="lg" hide-footer title="Ürün Görseli" v-model="showImageModal">
      <img :src="selectedImageUrl" class="img-fluid" />
    </b-modal>

    <!-- SOLDAN AÇILAN PANEL (BootstrapVue <b-sidebar>) -->
    <b-sidebar
        id="category-sidebar"
        v-if="!useHoverRail"
        v-model="showCategorySidebar"
        shadow
        backdrop
        :width="'320px'"
    >
      <b-form-input size="sm" v-model="categorySearch" placeholder="Kategori ara..." class="mb-2" />
      <div class="list-group category-list">
        <button
            v-for="cat in filteredCategories"
            :key="cat.id"
            type="button"
            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
            @click="selectCategory(cat.id)"
        >
          <span>{{ cat.name }}</span>
          <span v-if="selectedCategoryId === cat.id" class="badge bg-primary">Seçili</span>
        </button>
      </div>
    </b-sidebar>

    <!-- HOVER RAIL + DRAWER (soldan, hover ile açılır) -->
    <div
        v-if="useHoverRail"
        class="category-rail"
        @mouseenter="isHoverOpen = true"
        @mouseleave="isHoverOpen = false"
        @click="isHoverOpen = true"
    >
      <div class="rail-label">Kategoriler</div>
    </div>

    <div
        v-if="useHoverRail"
        class="category-hover-drawer compact"
        :class="{ open: isHoverOpen }"
        @mouseenter="isHoverOpen = true"
        @mouseleave="isHoverOpen = false"
    >
      <b-form-input size="sm" v-model="categorySearch" placeholder="Kategori ara..." class="mb-2" />
      <div class="list-group category-list">
        <button
            v-for="cat in filteredCategories"
            :key="cat.id"
            type="button"
            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
            @click="selectCategory(cat.id)"
        >
          <span>{{ cat.name }}</span>
          <span v-if="selectedCategoryId === cat.id" class="badge bg-primary">Seçili</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import OrderService from '../../service/OrderService';
import CategoryService from "@/service/CategoryService";
import { parseTRY, formatTRY } from "@/utils/MoneyUtils";

export default {
  name: 'product-basket-list',
  data() {
    return {
      totalRecords: 0,
      rows: [],
      columns: [
        {field: 'id', hidden: true},
        {label: 'Görsel', field: 'iconUrl', tdClass: 'text-center', thClass: 'text-center', sortable: false},
        {field: 'url', hidden: true},
        {label: 'Ürün Adı', field: 'name', sortable: false, filterOptions: {enabled: true, placeholder: 'Ürün Ara', trigger: 'enter'}},
        {label: 'Fiyat', field: 'listPriceText', tdClass: 'text-right text-nowrap', sortable: false},
        {label: 'KDV', field: 'vatRateText', tdClass: 'text-right text-nowrap', sortable: false},
        {label: 'Birim', field: 'unit', sortable: false},
        {field: 'productOptions', hidden: true},
        {field: 'price', hidden: true},
        {field: 'listPrice', hidden: true},
        {field: 'actions', sortable: false}
      ],
      serverParams: {
        columnFilters: {},
        sort: { field: '', type: '' },
        page: 1
      },
      basket: [],
      loading: false,
      quantitySelections: {},
      showImageModal: false,
      selectedImageUrl: null,
      categories: [],
      selectedCategoryId: null,
      showCategorySidebar: false,
      categorySearch: '',
      useHoverRail: true,
      isHoverOpen: false
    };
  },
  computed: {
    totalBasketPrice() {
      const total = this.basket.reduce((sum, item) => sum + item.totalPrice, 0);
      return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
      }).format(total);
    },
    filteredCategories() {
      if (!this.categorySearch) return this.categories;
      const q = this.categorySearch.toLowerCase();
      return this.categories.filter(c => (c.name || '').toLowerCase().includes(q));
    },
    selectedCategoryName() {
      const found = this.categories.find(c => c.id === this.selectedCategoryId);
      return found ? found.name : '';
    }
  },
  methods: {
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },
    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems(this.serverParams);
    },
    onColumnFilter(params) {
      this.updateParams({ columnFilters: params });
      this.loadItems(this.serverParams);
    },
    async loadItems(params) {
      if (!this.selectedCategoryId) {
        this.$alert(this, 'Lütfen kategori seçiniz.');
        return;
      }
      this.loading = true;
      try {
        const mergedParams = {
          ...params,
          columnFilters: {
            ...params.columnFilters,
            categoryId: this.selectedCategoryId,
            clientId: this.$store.state.clientId,
          }
        };
        const response = await CategoryService.getCategoryProductList(mergedParams);
        this.rows = response.data;
        this.totalRecords = response.total;
      } catch (error) {
        console.error("Listeleme sırasında hata:", error);
      } finally {
        this.loading = false;
      }
    },
    async getOrder() {
      this.loading = true;
      try {
        this.basket = await OrderService.getClientOrder(this.$store.state.clientId);
      } catch (error) {
        console.error("Basket çekilirken hata:", error);
      } finally {
        this.loading = false;
      }
    },
    async addToBasket(product) {
      let qty = this.quantitySelections[product.id];
      if (product.productOptions && product.productOptions.length > 0 && !qty) {
        qty = product.productOptions[0];
      }
      if (!product.productOptions || product.productOptions.length === 0) {
        qty = qty || 1;
      }
      const existing = this.basket.find(p => p.id === product.id);
      if (existing) {
        existing.quantity += qty;
        existing.totalPrice = existing.quantity * product.price;
      } else {
        this.basket.push({
          id: product.id,
          name: product.name,
          quantity: qty,
          price: product.price,
          totalPrice: qty * product.price
        });
      }
      await this.updateOrder();
      this.$set(this.quantitySelections, product.id, null);
    },
    async removeFromBasket(product) {
      let qty = this.quantitySelections[product.id];
      if (product.productOptions && product.productOptions.length > 0 && !qty) {
        qty = product.productOptions[0];
      }
      if (!product.productOptions || product.productOptions.length === 0) {
        qty = qty || 1;
      }
      const index = this.basket.findIndex(p => p.id === product.id);
      if (index !== -1) {
        if (this.basket[index].quantity > qty) {
          this.basket[index].quantity -= qty;
          this.basket[index].totalPrice = this.basket[index].quantity * product.price;
        } else {
          this.basket.splice(index, 1);
        }
      }
      await this.updateOrder();
      this.$set(this.quantitySelections, product.id, null);
    },
    async confirmCreateOrder() {
      await this.createOrder();
    },
    async createOrder() {
      let orderDate = '';
      try {
        this.loading = true;
        const request = {
          clientId: this.$store.state.clientId,
          orderItems: this.basket.map(item => ({
            productId: item.id,
            quantity: item.quantity,
            price: item.price.toFixed(4)
          }))
        };
        const response = await OrderService.create(request);
        this.$store.commit('setBalanceText', response.balanceText);
        orderDate = this.parseOrderDate(response.orderDate);
        this.basket = [];
        this.$notification(this, "Sipariş başarıyla oluşturuldu.");
      } catch (error) {
        if (error && error.message === 'Bakiyeniz sipariş oluşturmak için yeterli değil.') {
          const basketTotal = this.basket.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
          const currentBalance = parseTRY(this.$store.state.balanceText);
          const shortageRaw = basketTotal - currentBalance;
          const shortage = shortageRaw > 0 ? shortageRaw : basketTotal;
          const shortageRounded = Math.round(shortage * 100) / 100;
          const message =
              `${error.message}` +
              `\n<div style="margin-top:12px;text-align:center;">` +
              `<span style="display:inline-block;padding:8px 14px;border:2px dashed #dc3545;` +
              `border-radius:8px;background:#fff5f5;color:#dc3545;font-size:1.1rem;">` +
              `Eksik bakiye: <span style="font-size:1.35rem;font-weight:700;">` +
              `${formatTRY(shortageRounded)}</span></span></div>`;

          this.$alert(this, message, 'Uyarı', {
            secondaryActionLabel: 'Havale İle Öde',
            secondaryActionHandler: () => {
              const iban = 'TR12 0006 4000 0014 2080 8527 64';
              this.$alert(
                this,
                `<div style="margin-top:8px;text-align:center;">` +
                `<p style="font-size:1rem;margin-bottom:6px;">Aşağıdaki IBAN'a havale yapabilirsiniz:</p>` +
                `<div style="display:inline-flex;align-items:center;gap:6px;padding:8px 12px;border:2px solid #0d6efd;` +
                `border-radius:8px;background:#f0f4ff;white-space:nowrap;">` +
                `<span style="color:#0d6efd;font-size:1.05rem;font-weight:700;letter-spacing:1px;user-select:all;">` +
                `${iban}</span>` +
                `<img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%230d6efd' viewBox='0 0 16 16'%3E%3Crect x='5' y='5' width='10' height='10' rx='1' ry='1' stroke='%230d6efd' stroke-width='1.2' fill='none'/%3E%3Crect x='1' y='1' width='10' height='10' rx='1' ry='1' stroke='%230d6efd' stroke-width='1.2' fill='%23f0f4ff'/%3E%3C/svg%3E" ` +
                `alt="Kopyala" title="Kopyala" style="width:18px;height:18px;cursor:pointer;flex-shrink:0;" ` +
                `onclick="var t='${iban}';navigator.clipboard.writeText(t).then(function(){var el=event.target;el.style.opacity='0.4';setTimeout(function(){el.style.opacity='1';},800);})"/>` +
                `</div></div>`,
                'Havale Bilgisi'
              );
            },
            actionLabel: 'Gerekli Bakiyeyi Yükle ve Sipariş Oluştur',
            actionHandler: () => {
              this.$store.commit('setPendingOrder', {
                clientId: this.$store.state.clientId,
                prefillAmount: shortageRounded,
              });
              this.$router.push({ name: 'BalanceWithCard' });
            }
          });
        } else {
          this.$alert(this, error.message);
        }
      } finally {
        this.loading = false;
      }

      if (orderDate) {
        const cutoff = new Date(
            orderDate.getFullYear(),
            orderDate.getMonth(),
            orderDate.getDate(),
            15, 0, 0, 0
        );
        if (orderDate >= cutoff) {
          this.$alert(this, "15:00 sonrası gönderilen siparişler işleme alınmamaktadır. Lütfen siparişlerinizi 15:00'ten önce oluşturunuz.");
        }
      }
    },
    parseOrderDate(str) {
      if (!str) return null;
      const [datePart, timePart] = str.split(" ");
      const [dd, MM, yyyy] = datePart.split("/").map(n => parseInt(n, 10));
      const [HH, mm] = timePart.split(":").map(n => parseInt(n, 10));
      return new Date(yyyy, MM - 1, dd, HH, mm, 0, 0);
    },
    async updateOrder() {
      try {
        this.loading = true;
        const request = {
          clientId: this.$store.state.clientId,
          orderItems: this.basket.map(item => ({
            productId: item.id,
            quantity: item.quantity,
            price: item.price.toFixed(4)
          }))
        };
        await OrderService.update(request);
      } catch (error) {
        this.$alert(this, error.message);
      } finally {
        this.loading = false;
      }
    },
    async clearBasket() {
      this.basket = [];
      await this.updateOrder();
      this.quantitySelections = {};
    },
    async removeItemFromBasket(productId) {
      this.basket = this.basket.filter(item => item.id !== productId);
      await this.updateOrder();
    },
    openImageModal(imageUrl) {
      if (!imageUrl) return;
      this.selectedImageUrl = imageUrl;
      this.showImageModal = true;
    },
    async loadCategories() {
      try {
        this.categories = await CategoryService.getCategories();
      } catch (e) {
        console.error("Kategori yükleme hatası:", e);
      }
    },
    async onCategoryChange() {
      const newFilters = {
        ...this.serverParams.columnFilters,
        categoryId: this.selectedCategoryId
      };
      this.updateParams({ columnFilters: newFilters });
      await this.loadItems(this.serverParams);
    },
    formatPrice(price) {
      return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
      }).format(price);
    },
    closeCategoryPanel() {
      this.showCategorySidebar = false;
    },
    async selectCategory(id) {
      this.selectedCategoryId = id;
      this.isHoverOpen = false;
      this.closeCategoryPanel();
      await this.onCategoryChange();
    }
  },
  beforeMount() {
    const pending = this.$store.state.pendingOrder;
    if (pending && pending.expiresAt && pending.expiresAt < Date.now()) {
      this.$store.commit('clearPendingOrder');
    }
    this.loadCategories();
    this.getOrder();
  }
};
</script>

<style>
.card {
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

/* Üstten açılan top-sheet için animasyon ve stil */
.slide-down-enter-active, .slide-down-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.slide-down-enter, .slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
.category-top-sheet {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #fff;
  z-index: 1051; /* modal üstü */
  padding: 1rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  max-height: 60vh;
  overflow: auto;
}
.category-top-sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  z-index: 1050;
}

/* Sidebar içi kategori listesi scroll davranışı */
.category-list {
  max-height: calc(100vh - 160px);
  overflow: auto;
}

/* Hover rail + drawer */
.category-rail {
  position: fixed;
  top: 40%;
  left: 0;
  transform: translateY(-50%);
  width: 42px;
  height: 250px;
  background: #fff;
  border: 1px solid #000;        /* tüm kenarlar siyah */
  border-radius: 10px;           /* tüm köşeler yuvarlak */
  box-sizing: border-box;        /* genişlik/height içine border dahil */
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  z-index: 1049;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.category-rail .rail-label {
  writing-mode: vertical-rl;
  text-orientation: upright;     /* yukarıdan aşağı, harfler dik */
  font-size: 12px;
  letter-spacing: .08em;
  color: #6c757d;
  white-space: nowrap;
}

.category-hover-drawer {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 0;
  padding: 0;
  background: #fff;
  box-shadow: 0 0 0 rgba(0,0,0,0);
  overflow: hidden;
  z-index: 1050;
  transition: width .2s ease, box-shadow .2s ease, padding .2s ease;
  pointer-events: none;
}
.category-hover-drawer.open {
  width: 350px;
  padding: 1rem;
  box-shadow: 4px 0 18px rgba(0,0,0,.12);
  pointer-events: auto;
}

/* === YAN PANELİ (b-sidebar) KOMPAKTLAŞTIR === */
#category-sidebar {
  --sb-scale: 0.85; /* genel ölçek */
  font-size: calc(1rem * var(--sb-scale));
}
#category-sidebar .b-sidebar-body { padding: .75rem; }
#category-sidebar .form-control,
#category-sidebar .btn {
  font-size: .875rem;
  padding: .25rem .5rem;
  height: calc(1.5em + .5rem + 2px);
}
#category-sidebar .category-list { max-height: calc(100vh - 120px); }
#category-sidebar .category-list .list-group-item {
  font-size: .9em;
  line-height: 1.25;
  padding: .35rem .5rem;
}
#category-sidebar .category-list .list-group-item + .list-group-item {
  margin-top: .125rem;
}
#category-sidebar .category-list .badge {
  font-size: .65rem;
  padding: .2rem .35rem;
}

/* Sidebar başlığı: dikey yazı */
#category-sidebar .b-sidebar-header {
  display: flex;
  align-items: center;
  justify-content: center; /* başlığı ortaya hizala */
  min-height: 56px;        /* header biraz yüksek olsun */
}
#category-sidebar .sidebar-title-vertical {
  writing-mode: vertical-rl;
  text-orientation: upright;
  line-height: 1;
  display: inline-block;
  border: 2px solid #000;      /* istersen kaldırabilirsin */
  border-radius: 8px;
  padding: .25rem .35rem;
}

/* === HOVER ÇEKMECEYİ DE KOMPAKTLAŞTIR === */
.category-hover-drawer.compact { font-size: .9rem; }
.category-hover-drawer.compact .form-control,
.category-hover-drawer.compact .btn {
  font-size: .85rem;
  padding: .25rem .5rem;
  height: calc(1.5em + .5rem + 2px);
}
.category-hover-drawer.compact .category-list { max-height: calc(100vh - 120px); }
.category-hover-drawer.compact .list-group-item {
  font-size: .9em;
  line-height: 1.25;
  padding: .35rem .5rem;
}
.category-hover-drawer.compact .badge {
  font-size: .65rem;
  padding: .2rem .35rem;
}
</style>
